import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import GearIcon from '@mui/icons-material/Settings';

import { EmployeeRole, Language, States } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Employee,
  EmployeeGroup,
  EmployeeLicenseItem,
  EmployeeLicenseSummary,
  PaginationType,
} from '@vestahealthcare/common/models';
import { DashPermission } from '@vestahealthcare/common/models/DashPermission';

import { FilterItem, IconButton, Panel, Tabs } from 'styleguide-v2';

import { showGlobalError } from 'dash/src/components/GlobalMessage';
import { CacheServices } from 'dash/src/services';
import {
  createEmployee,
  fetchEmployeeLicensesOverview,
  fetchEmployeeLicensesRN,
  fetchPaginatedEmployees,
  updateEmployee,
  updateEmployeeLicenseRN,
  updateEmployeeNLCStates,
} from 'dash/src/services/EmployeeServices';
import Session from 'dash/src/services/SessionServices';
import { getServerFilters } from 'dash/src/utils/filterUtils';

import { PodReportingStyles } from '../../Clinical/PodReporting/PodReportingStyles';
import { CompactStatesModal } from './CompactStatesModal';
import { EmployeeEditModal } from './EmployeeEdit';
import { EmployeeFiltersBar } from './EmployeeFiltersBar';
import { EmployeeFiltersModal } from './EmployeeFiltersModal';
import { EmployeeLicenseEdit } from './EmployeeLicenseEdit';
import { EmployeesLicenseOverviewPage } from './EmployeesLicenseOverviewPage';
import { EmployeeLicensesTable } from './EmployeesLicenseTable';
import { EmployeesTable } from './EmployeesTable';
import {
  EMPLOYEE_TABS,
  EMPLOYEE_TABS_LICENSE_DETAILS,
  EMPLOYEE_TABS_LIST,
  EMPLOYEE_TABS_OVERVIEW,
  KeyGetEmployeeParams,
} from './types';

let employeeFiltersString = '';

export const EmployeesDashboard = () => {
  const styles = PodReportingStyles();

  const [selectedTab, setSelectedTab] = useState<string>(EMPLOYEE_TABS_LIST);

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeLicenses, setEmployeeLicenses] = useState<
    EmployeeLicenseItem[]
  >([]);
  const [employeeLicenseOverview, setEmployeeLicenseOverview] = useState<
    EmployeeLicenseSummary
  >();

  const [leadEmployees, setLeadEmployees] = useState<Employee[]>([]);
  const [pagination, setPagination] = useState<PaginationType>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);

  const [groups, setGroups] = useState<EmployeeGroup[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [permissions, setPermissions] = useState<DashPermission[]>([]);
  const [nlcStates, setNLCStates] = useState<States[]>([]);

  const [newFilters, setNewFilters] = useState<
    {
      [x in KeyGetEmployeeParams]?: FilterItem;
    }
  >({});

  const [showConfigCompactModal, setShowConfigCompactModal] = useState<boolean>(
    false,
  );
  const [showEditEmployee, setShowEditEmployee] = useState<boolean>(false);
  const [showEditLicenseModal, setShowEditLicenseModal] = useState<boolean>(
    false,
  );
  const [modalData, setModalData] = useState<Employee>();
  const [editLicense, setEditLicense] = useState<EmployeeLicenseItem>();

  const getLeadEmployees = async () => {
    const { items } = await fetchPaginatedEmployees({
      enabled: true,
      isLead: true,
      limit: 100,
    });
    setLeadEmployees(items);
  };

  const getInitialData = async () => {
    setLoadingData(true);
    const [groups, permissions, languages, nlcStates] = await Promise.all([
      CacheServices.getEmployeeGroupsAssignee(),
      CacheServices.getDashPermissions(),
      CacheServices.getLanguages(),
      CacheServices.getEmployeeNLCStates(),
    ]);
    setGroups(groups);
    setPermissions(permissions);
    setLanguages(languages);
    setNLCStates(nlcStates);
    setLoadingData(false);
  };

  const getEmployees = async () => {
    if (!newFilters) return;

    setLoading(true);
    const currentFilters = getServerFilters(newFilters);

    try {
      employeeFiltersString = JSON.stringify(currentFilters);

      const { items, pagination } = await fetchPaginatedEmployees({
        ...currentFilters,
        limit: pageSize,
        offset: page * pageSize,
        sort: 'fullName asc',
      });

      if (employeeFiltersString === JSON.stringify(currentFilters)) {
        if (page === 0) {
          setEmployees(items);
        } else {
          setEmployees([...employees, ...items]);
        }
        setPagination(pagination);
      }
    } catch (e) {
      showGlobalError(e as string);
    }
    if (employeeFiltersString === JSON.stringify(currentFilters)) {
      setLoading(false);
    }
  };

  const getEmployeeLicensesOverview = async () => {
    if (!newFilters) return;

    setLoading(true);
    const currentFilters = getServerFilters(newFilters);

    try {
      employeeFiltersString = JSON.stringify(currentFilters);

      const overview = await fetchEmployeeLicensesOverview({
        ...currentFilters,
      });

      if (employeeFiltersString === JSON.stringify(currentFilters)) {
        setEmployeeLicenseOverview(overview);
      }
    } catch (e) {
      showGlobalError(e as string);
    }
    if (employeeFiltersString === JSON.stringify(currentFilters)) {
      setLoading(false);
    }
  };

  const getEmployeeLicensesRN = async () => {
    if (!newFilters) return;

    setLoading(true);
    const currentFilters = getServerFilters(newFilters);

    try {
      employeeFiltersString = JSON.stringify(currentFilters);

      const { items, pagination } = await fetchEmployeeLicensesRN({
        ...currentFilters,

        enabled: true,
        role: [EmployeeRole.RN.value],
        limit: pageSize,
        offset: page * pageSize,
        sort: 'fullName asc',
      });

      if (employeeFiltersString === JSON.stringify(currentFilters)) {
        if (page === 0) {
          setEmployeeLicenses(items);
        } else {
          setEmployeeLicenses([...employeeLicenses, ...items]);
        }

        setPagination(pagination);
      }
    } catch (e) {
      showGlobalError(e as string);
    }
    if (employeeFiltersString === JSON.stringify(currentFilters)) {
      setLoading(false);
    }
  };

  const getData = () => {
    if (selectedTab === EMPLOYEE_TABS_LIST) {
      getEmployees();
    } else if (selectedTab === EMPLOYEE_TABS_OVERVIEW) {
      getEmployeeLicensesOverview();
    } else if (selectedTab === EMPLOYEE_TABS_LICENSE_DETAILS) {
      getEmployeeLicensesRN();
    }
  };

  useEffect(() => {
    getInitialData();
    getLeadEmployees();
  }, []);

  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    } else {
      getData();
    }
  }, [newFilters, pageSize, selectedTab]);

  useEffect(() => {
    getData();
  }, [page]);

  const addEmployee = () => {
    setModalData(new Employee({}));
    setShowEditEmployee(true);
  };

  return (
    <Panel>
      <Panel.Heading title={translate('employees.title')} filtersV2>
        {Session.actingUser.isAdminRole && (
          <Panel.Actions>
            <IconButton
              onClick={() => setShowConfigCompactModal(true)}
              tooltip={translate('employees.compactStatesConfig')}
            >
              <GearIcon fontSize="large" />
            </IconButton>
            <IconButton
              onClick={addEmployee}
              tooltip={translate('employees.add')}
            >
              <AddIcon fontSize="large" />
            </IconButton>
          </Panel.Actions>
        )}
        <Panel.FilterBar
          onClearFilters={() => {
            setPage(0);
            setNewFilters({});
          }}
          onDeleteFilter={(key: string) => {
            setPage(0);
            setNewFilters({
              ...newFilters,
              [key]: undefined,
            });
          }}
          onOpenFilters={() => setOpen(!open)}
          chips={{
            ...newFilters,
            enabled:
              selectedTab !== EMPLOYEE_TABS_LICENSE_DETAILS
                ? newFilters.enabled
                : undefined,
            role:
              selectedTab !== EMPLOYEE_TABS_LICENSE_DETAILS
                ? newFilters.role
                : undefined,
          }}
          inputs={
            <EmployeeFiltersBar
              data={{
                employeeGroups: EmployeeGroup.toSelectable(groups),
                permissions: DashPermission.toSelectable(permissions),
              }}
              disabledFields={
                selectedTab === EMPLOYEE_TABS_LICENSE_DETAILS
                  ? ['enabled', 'role']
                  : undefined
              }
              loading={loadingData}
              onChange={setNewFilters}
              filters={newFilters}
            />
          }
        />
        <Panel.Tabs>
          <Tabs
            disabled={loading}
            extraPadding
            items={EMPLOYEE_TABS}
            onChange={setSelectedTab}
            value={selectedTab}
          />
        </Panel.Tabs>
      </Panel.Heading>
      <Panel.Body
        loading={loading}
        className={
          selectedTab === EMPLOYEE_TABS_OVERVIEW
            ? classNames(styles.whiteBackground, styles.paddingBody)
            : undefined
        }
      >
        <EmployeeFiltersModal
          data={{
            employeeGroups: EmployeeGroup.toSelectable(groups),
            languages: Language.toSelectable(languages),
            leadEmployees: Employee.toSelectable(leadEmployees),
            permissions: DashPermission.toSelectable(permissions),
          }}
          disabledFields={
            selectedTab === EMPLOYEE_TABS_LICENSE_DETAILS
              ? ['enabled', 'role']
              : undefined
          }
          filters={newFilters}
          loadingFilters={loadingData}
          onChange={setNewFilters}
          onClose={() => setOpen(false)}
          open={open}
        />
        {selectedTab === EMPLOYEE_TABS_OVERVIEW && (
          <EmployeesLicenseOverviewPage
            employeeLicenseOverview={employeeLicenseOverview}
            onChangeFilters={setNewFilters}
            onChangeSelectedTab={setSelectedTab}
          />
        )}
        {selectedTab === EMPLOYEE_TABS_LIST && (
          <EmployeesTable
            defaultPageSize={pageSize}
            employees={employees}
            onChangePage={setPage}
            onChangeRowsPerPage={(size) => {
              setPage(0);
              setPageSize(size);
            }}
            onEdit={(employee) => {
              setModalData(employee);
              setShowEditEmployee(true);
            }}
            pagination={pagination}
          />
        )}

        {selectedTab === EMPLOYEE_TABS_LICENSE_DETAILS && (
          <EmployeeLicensesTable
            defaultPageSize={pageSize}
            licenses={employeeLicenses}
            onChangePage={setPage}
            onChangeRowsPerPage={(size) => {
              setPage(0);
              setPageSize(size);
            }}
            onEdit={(license) => {
              setEditLicense(license);
              setShowEditLicenseModal(true);
            }}
            pagination={pagination}
          />
        )}
        <EmployeeEditModal
          employee={modalData}
          employees={leadEmployees}
          open={showEditEmployee}
          onClose={() => setShowEditEmployee(false)}
          onCreate={async (params) => {
            await createEmployee(params);
            CacheServices.invalidateEmployees();
            if (page !== 0) {
              setPage(0);
            } else {
              await getEmployees();
              await getLeadEmployees();
            }
          }}
          onEdit={async (id, params) => {
            await updateEmployee(id, params);
            CacheServices.invalidateEmployees();
            if (Session.actingUser.id === id) {
              await Session.fetchSessionInformation();
            }
            if (page !== 0) {
              setPage(0);
            } else {
              await getEmployees();
              await getLeadEmployees();
            }
          }}
        />
        <CompactStatesModal
          states={nlcStates}
          open={showConfigCompactModal}
          onClose={() => setShowConfigCompactModal(false)}
          onSubmit={async (params) => {
            await updateEmployeeNLCStates(params);
            CacheServices.invalidateEmployeeNLCStates();
            setLoading(true);
            await getInitialData();
            getData();
          }}
        />
        <EmployeeLicenseEdit
          data={{
            compactStates: nlcStates,
          }}
          license={editLicense}
          open={showEditLicenseModal}
          onClose={() => {
            setShowEditLicenseModal(false);
            setTimeout(() => setEditLicense(undefined), 500);
          }}
          onSubmit={async (id, params) => {
            await updateEmployeeLicenseRN(id, params);
            getData();
          }}
        />
      </Panel.Body>
    </Panel>
  );
};

export default EmployeesDashboard;
